/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import * as joint from '@joint/plus';
import EventSequence from '../../@types/event-sequence';
import * as canvasActions from './canvas-actions';
import {CanvasController} from './canvas-controller';
import {EsdEvent} from '../../@types/esdEvent';
import {FaultTreeNode} from '../../@types/faultTreeNode';
import TagService from '../../services/tag-service';
import Tag, {TagLinks} from '../../@types/tag';
import {
    EventSequenceOccurenceStatsPerNode,
    EventSequenceOccurrenceStats,
    EventSequencePsfTemplateItemStats,
    PsfTemplate,
    PsfTemplateItem,
    PsfTemplateItemWithCount
} from '../../@types/occurrence-dna';
import {IntegratedRiskPicture, IRPNodeMappingDTO, ReportMappingUpdateDTO} from '../../@types/integrated-risk-picture';
import {SimpleLinkedOccurrenceDna} from '../../@types/mapping-dna';
import {toast} from 'react-toastify';
import {setPsfNode} from '../isam-nodes';
import irpService from '../../services/irp.service';
import {FaultTreeViewType} from '../../@types/fault-tree-view-type';
import {faultTreeViewRegistry, FaultTreeViewStrategy} from './views';
import type {FaultTreeContainerRef} from './fault-tree-container';

export class CanvasEnvironment {
    public controller: CanvasController;
    public paper: joint.dia.Paper;
    public graph: joint.dia.Graph;
    public paperScroller: joint.ui.PaperScroller;
    public selection: joint.dia.Element[] = [];
    public activeTopNode: joint.dia.Element | null = null;
    public eventSequence: EventSequence | null = null;
    /**
     * When set, the canvas edits this container's fault tree instead of an Event Sequence's --
     * the shared fault-tree-node modals route their mutations through it. Left null for the ESD
     * canvas (its behaviour is then unchanged).
     */
    public faultTreeContainer: FaultTreeContainerRef | null = null;
    public isDirty = false;
    private resizeObserver: ResizeObserver | null = null;
    private contextMenuHandler: ((event: Event) => void) | null = null;
    public allGroupTags: Tag[] = [];
    public esdTagLinks: TagLinks = { eventTags: [], faultTreeNodeTags: [] };
    // Pre-built lookup Maps for O(1) tag access (instead of O(n) .filter())
    public tagLinksByEventId: Map<number, number[]> = new Map();
    public tagLinksByFtNodeId: Map<number, number[]> = new Map();
    public tagById: Map<number, Tag> = new Map();
    public isTagsModeActive: boolean = true;
    public isCalloutsModeActive: boolean = false;
    public isOdnaModeActive: boolean = false;
    public readonly: boolean = false;
    public fullscreenCallback: (() => void) | null = null;

    // Lazy load configuration for fault trees
    public lazyLoadDepth: number = 3;
    public loadAllFT: boolean = false;

    // Fault tree view configuration - initialize from localStorage if available
    public currentViewType: FaultTreeViewType = (() => {
        const saved = localStorage.getItem('ftViewType');
        return (saved as FaultTreeViewType) || FaultTreeViewType.STANDARD;
    })();

    // Track which view type was used when tools were last rendered
    // Used to detect view type mismatch on tab switch and re-add tools
    public renderedViewType: FaultTreeViewType | null = null;

    /**
     * Get the current view strategy for fault tree visualization.
     */
    public get currentViewStrategy(): FaultTreeViewStrategy {
        return faultTreeViewRegistry.get(this.currentViewType);
    }

    /**
     * Set the current view type for fault tree visualization.
     * This triggers lifecycle hooks on the old and new view strategies.
     */
    public setViewType(viewType: FaultTreeViewType): void {
        if (this.currentViewType === viewType) return;

        // Call deactivate hook on old view
        const oldView = this.currentViewStrategy;
        oldView.onDeactivate?.(this);

        // Update the view type
        this.currentViewType = viewType;

        // Call activate hook on new view
        const newView = this.currentViewStrategy;
        newView.onActivate?.(this);
    }

    // Lazy decoration tracking - track which elements have been decorated
    private decoratedOdnaElements: Set<string> = new Set();
    private decoratedTagElements: Set<string> = new Set();
    private decoratedCalloutElements: Set<string> = new Set();

    // Track expanded lazy-loaded FT nodes per event (keyed by event ID, value is Set of FT node IDs)
    public expandedLazyNodeIdsByEvent: Map<number, Set<number>> = new Map();
    private decorationScrollTimeout: ReturnType<typeof setTimeout> | null = null;
    // Track pending RAF for fault tree expansion decorations
    private pendingDecorationRafId: number | null = null;
    // Track pending timeout for fault tree expansion (loading spinner)
    private pendingExpansionTimeoutId: ReturnType<typeof setTimeout> | null = null;
    // Generation counter to invalidate async callbacks when collapse happens
    public expansionGeneration: number = 0;

    private currentIntegratedRiskPicture: IntegratedRiskPicture | null = null;
    public currentLinkedOccurrenceDna: SimpleLinkedOccurrenceDna | null = null;

    public psfTemplate: PsfTemplate | null = null;
    // Pre-built lookup Map for O(1) PSF template item access (instead of O(n) .find())
    public psfTemplateItemById: Map<number, PsfTemplateItem> = new Map();
    public psfCounts: Map<number, Array<PsfTemplateItemWithCount>> = new Map();
    public psfSelectedIds: number[] = [];
    public occurrenceSelectedIds: number[] = [];
    // Cache of PSF node IDs for O(1) removal (avoids full graph scan)
    public psfNodeIds: Set<string> = new Set();
    public eventSequenceOccurrenceStats: EventSequenceOccurrenceStats | null = null;
    // Pre-built lookup Maps for O(1) ODNA stat access (instead of O(n) .find())
    public odnaStatByEventId: Map<number, EventSequenceOccurenceStatsPerNode> = new Map();
    public odnaStatByFtNodeId: Map<number, EventSequenceOccurenceStatsPerNode> = new Map();

    public dnaPath: joint.dia.Cell[] = [];
    public mask = joint.highlighters.mask;

    public HighlighterFrame = joint.dia.HighlighterView.extend({
        tagName: 'rect',
        attributes: {
            'stroke': '#ed2637',
            'stroke-width': 2,
            'fill': 'none',
            'pointer-events': 'none',
        },
    
        // Method called to highlight a CellView
        highlight(cellView: joint.dia.CellView) {
            const { padding = 0 } = this.options;
        
            const bbox = cellView.model.getBBox();
            // Highlighter is always rendered relatively to the CellView origin
            bbox.x = bbox.y = 0;
            // Increase the size of the Highlighter
            bbox.inflate(padding);
            this.vel.attr(bbox.toJSON());
        },
    });

    public contextMenuCallback: (evt: joint.dia.Event, elementView: joint.dia.ElementView) => void;
    public setSelectedISAMNode: (node: EsdEvent | FaultTreeNode) => void;
    public setLoading: (loading: boolean) => void;
    // Flag to indicate we're restoring state on tab activation (skip loading spinner)
    public isRestoringState: boolean = false;

    constructor(paperElement: Element,
        contextMenuCallback: (evt: joint.dia.Event, elementView: joint.dia.ElementView) => void,
        setSelectedISAMNode: (node: EsdEvent | FaultTreeNode) => void,
        setLoading: (loading: boolean) => void,
        readonly: boolean) {
        this.contextMenuCallback = contextMenuCallback;
        this.setSelectedISAMNode = setSelectedISAMNode;
        this.setLoading = setLoading;
        this.readonly = readonly;

        this.graph = new joint.dia.Graph({}, { cellNamespace: canvasActions.allshapes });
        this.paper = new joint.dia.Paper({
            model: this.graph,
            gridSize: 1,
            defaultConnectionPoint: {name: 'boundary', args: {offset: 5}},
            defaultAnchor: { name: 'modelCenter' },
            defaultConnector: { name: 'normal' },
            async: true,
            asyncBatchSize: 50,  // Render 50 cells per frame to avoid blocking
            frozen: true,
            interactive: function(cellView: joint.dia.CellView): any {
                if (cellView.model.get('nodeClass') !== 'CALLOUTNODE' && cellView.model.get('nodeClass') !== 'PSFNODE') return {
                    linkMove: false,
                    labelMove: false,
                    elementMove: false
                };
                return {
                    linkMove: false,
                    labelMove: false,
                    elementMove: true
                };
            
            },
            sorting: joint.dia.Paper.sorting.APPROX,
            cellViewNamespace: {...canvasActions.allshapes},
            background: {
                color: '#F8F9FA',
                image: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAIElEQVQoU2NkIALcuvU/jZEIdQyjCvGGEjh4QAQxYQkAIfcfBcQyCEYAAAAASUVORK5CYII=',
                repeat: 'repeat',
                size: '20px 20px'
            }
        });
        this.paperScroller = new joint.ui.PaperScroller({
            paper: this.paper,
            padding: { top: 50, left: 480 },
            scrollWhileDragging: true,
            inertia: true,
            autoResizePaper: true,
            borderless: true,
            baseWidth: 1000,
            baseHeight: 1000,
            contentOptions: () => {
                const minArea = new joint.g.Rect(0, 0, 4000, 3000);
                const contentArea = minArea.union(this.graph.getBBox()!);
                return {
                    contentArea
                }
            }
        });
        paperElement.appendChild(this.paperScroller.el);
        this.paperScroller.render();
        this.paperScroller.zoom(1.0, { absolute: true });
        

        this.enableVirtualRendering();

        this.controller = new CanvasController(this);

        // disable the default browser context menu (scoped to canvas only)
        // Must be on paper.el to intercept the native contextmenu event that fires on mouseup
        // See: https://github.com/clientIO/joint/discussions/1890
        this.contextMenuHandler = (event: Event): void => {
            event.preventDefault();
        };
        this.paper.el.addEventListener('contextmenu', this.contextMenuHandler);

    }

    enableVirtualRendering() {
        let { paper, paperScroller: scroller, resizeObserver } = this;
        const viewportOverlap = 50;

        let viewportArea: joint.g.Rect;
        function updateViewportArea() {
            viewportArea = scroller.getVisibleArea().inflate(viewportOverlap);
        }

        updateViewportArea();

        // Watch viewport area and lazy decorate visible elements
        scroller.el.onscroll = () => {
            updateViewportArea();
            this.onScrollDecorateVisible();
        };
        paper.on('scale', updateViewportArea);
        paper.on('blank:pointerdblclick', () => {
            if (this.fullscreenCallback) {
                this.fullscreenCallback();
            }
        });


        paper.options.viewport = (view: joint.mvc.View<any>) => {
            const { model } = view;
            if (model.get('hidden')) return false;
            const bbox = model.getBBox();
            if (model.isLink()) {
                // Vertical/horizontal links have zero width/height.
                bbox.inflate(1);
            }
            return viewportArea.intersect(bbox) !== null;
        };

        resizeObserver = new ResizeObserver(() => {
            updateViewportArea();
        });

        resizeObserver.observe(scroller.el.parentNode as Element);

    }

    public destroy(): void {
        if (this.contextMenuHandler) {
            this.paper.el.removeEventListener('contextmenu', this.contextMenuHandler);
            this.contextMenuHandler = null;
        }
        if (this.decorationScrollTimeout) {
            clearTimeout(this.decorationScrollTimeout);
            this.decorationScrollTimeout = null;
        }
        if (this.pendingDecorationRafId !== null) {
            cancelAnimationFrame(this.pendingDecorationRafId);
            this.pendingDecorationRafId = null;
        }
        if (this.pendingExpansionTimeoutId !== null) {
            clearTimeout(this.pendingExpansionTimeoutId);
            this.pendingExpansionTimeoutId = null;
        }
        this.paper.remove();
        this.paperScroller.remove();
        this.resizeObserver?.disconnect();
    }

    public setPendingDecorationRaf(rafId: number | null): void {
        // Cancel any existing RAF before setting new one
        if (this.pendingDecorationRafId !== null) {
            cancelAnimationFrame(this.pendingDecorationRafId);
        }
        this.pendingDecorationRafId = rafId;
    }

    public setPendingExpansionTimeout(timeoutId: ReturnType<typeof setTimeout> | null): void {
        // Cancel any existing timeout before setting new one
        if (this.pendingExpansionTimeoutId !== null) {
            clearTimeout(this.pendingExpansionTimeoutId);
        }
        this.pendingExpansionTimeoutId = timeoutId;
    }

    /**
     * Rebuild the tagById, tagLinksByEventId, and tagLinksByFtNodeId Maps
     * from the current allGroupTags and esdTagLinks arrays.
     * Call this after any in-place mutation of tag data.
     */
    public rebuildTagMaps(): void {
        this.tagById.clear();
        this.tagLinksByEventId.clear();
        this.tagLinksByFtNodeId.clear();

        for (const tag of this.allGroupTags) {
            if (tag.id != null) {
                this.tagById.set(tag.id, tag);
            }
        }

        for (const tagLink of this.esdTagLinks.eventTags) {
            const existing = this.tagLinksByEventId.get(tagLink.eventId);
            if (existing) {
                existing.push(tagLink.tagId);
            } else {
                this.tagLinksByEventId.set(tagLink.eventId, [tagLink.tagId]);
            }
        }

        for (const tagLink of this.esdTagLinks.faultTreeNodeTags) {
            const existing = this.tagLinksByFtNodeId.get(tagLink.faultTreeNodeId);
            if (existing) {
                existing.push(tagLink.tagId);
            } else {
                this.tagLinksByFtNodeId.set(tagLink.faultTreeNodeId, [tagLink.tagId]);
            }
        }
    }

    public fetchTagsAndTagLinks(): Promise<void> {
        // fetch tags and tag links
        const groupId = this.eventSequence?.group?.id;
        const esdId = this.eventSequence?.id;

        return Promise.all([TagService.getGroupTags(groupId!), TagService.getEventSequenceTags(esdId!)]).then((values) => {
            this.allGroupTags = values[0];
            this.esdTagLinks = values[1];

            this.rebuildTagMaps();

            return;
        }).catch((error) => {
            throw new Error('Tag returned an error: ' + error);
        });
    }

    public drawDnaPath(): void {
        if (this.currentLinkedOccurrenceDna) {
            const dna = this.currentLinkedOccurrenceDna;

            // Single pass categorization of DNA nodes
            const dnaPathEventFailure: joint.dia.Element[] = [];
            const dnaPathEventSuccess: joint.dia.Element[] = [];
            const dnaPathFtFailure: joint.dia.Element[] = [];
            const dnaPathFtSuccess: joint.dia.Element[] = [];

            for (const node of dna.events) {
                const cell = this.graph.getCell('event-' + node.id) as joint.dia.Element;
                if (cell) {
                    if (node.failureStatus === 'FAILURE') {
                        dnaPathEventFailure.push(cell);
                    } else if (node.failureStatus === 'SUCCESS') {
                        dnaPathEventSuccess.push(cell);
                    }
                }
            }

            for (const node of dna.faultTreeNodes) {
                const cell = this.graph.getCell('ft-' + node.id) as joint.dia.Element;
                if (cell) {
                    if (node.failureStatus === 'FAILURE') {
                        dnaPathFtFailure.push(cell);
                    } else if (node.failureStatus === 'SUCCESS') {
                        dnaPathFtSuccess.push(cell);
                    }
                }
            }

            const dnaPathFailure = dnaPathEventFailure.concat(dnaPathFtFailure);
            const dnaPathSuccess = dnaPathEventSuccess.concat(dnaPathFtSuccess);

            // Build Sets for O(1) lookup instead of O(n) .find()
            const failureIdSet = new Set(dnaPathFailure.map(el => el.id));
            const successIdSet = new Set(dnaPathSuccess.map(el => el.id));

            // Helper to find links, accounting for embedded barriers in containers
            const findDnaLinks = (dnaElements: joint.dia.Element[], dnaIdSet: Set<string | number>): joint.dia.Cell[] => {
                const links: joint.dia.Cell[] = [];
                const addedLinkIds = new Set<string | number>();

                for (const element of dnaElements) {
                    const containerId = element.get('containerId') as string | undefined;

                    // Standard outbound links from element
                    for (const link of this.graph.getConnectedLinks(element, { outbound: true })) {
                        if (addedLinkIds.has(link.id)) continue;
                        const target = link.getTargetElement();
                        if (target && dnaIdSet.has(target.id)) {
                            links.push(link);
                            addedLinkIds.add(link.id);
                        }
                    }

                    // If element is embedded in a container, also check container's links
                    if (containerId) {
                        const container = this.graph.getCell(containerId) as joint.dia.Element;
                        if (container) {
                            // Inbound links to container (from previous DNA node)
                            for (const link of this.graph.getConnectedLinks(container, { inbound: true })) {
                                if (addedLinkIds.has(link.id)) continue;
                                const source = link.getSourceElement();
                                if (source && dnaIdSet.has(source.id)) {
                                    links.push(link);
                                    addedLinkIds.add(link.id);
                                }
                            }
                            // Outbound links from container (to next DNA node)
                            for (const link of this.graph.getConnectedLinks(container, { outbound: true })) {
                                if (addedLinkIds.has(link.id)) continue;
                                const target = link.getTargetElement();
                                if (target && dnaIdSet.has(target.id)) {
                                    links.push(link);
                                    addedLinkIds.add(link.id);
                                }
                            }
                        }
                    }
                }
                return links;
            };

            const failedLinks = findDnaLinks(dnaPathFailure, failureIdSet);
            const successLinks = findDnaLinks(dnaPathSuccess, successIdSet);

            this.unhighlightDnaPath();
            this.highlightDnaPath(failedLinks.concat(dnaPathFailure), 'FAILURE');
            this.highlightDnaPath(successLinks.concat(dnaPathSuccess), 'SUCCESS');
            this.dnaPath = failedLinks.concat(dnaPathFailure).concat(dnaPathSuccess).concat(successLinks);
        }
    }

    private expandDnaPath(dna: SimpleLinkedOccurrenceDna): void {
        if (dna) {
            // Use Sets for O(1) lookups instead of O(n) .includes()
            const eventIdSet = new Set(dna.eventIds);
            const ftIdSet = new Set(dna.faultTreeNodeIds);

            // Find matching event with O(1) lookup
            const matchingEvent = this.eventSequence?.events.find((event) => eventIdSet.has(event.id));
            if (matchingEvent) {
                const eventElement = this.graph.getCell('event-' + matchingEvent.id) as joint.dia.Element;
                if (!this.paperScroller.isElementVisible(eventElement)) {
                    this.paperScroller.centerElement(eventElement);
                }
            } else {
                // Search for fault tree node
                for (const event of this.eventSequence?.events || []) {
                    if (!event.faultTree?.faultTreeNodes) continue;

                    const foundNode = event.faultTree.faultTreeNodes.find((ftNode: { id: number }) => ftIdSet.has(ftNode.id));
                    if (foundNode) {
                        const eventElement = this.graph.getCell('event-' + event.id) as joint.dia.Element;
                        if (!eventElement) continue;

                        if (eventElement.get('collapsed')) {
                            this.paper.trigger('element:expand', this.paper.findViewByModel(eventElement));
                        }
                        const ftElement = this.graph.getCell('ft-' + foundNode.id) as joint.dia.Element;
                        if (!ftElement) {
                            // FT not loaded yet (async expansion) - center on event instead
                            if (!this.paperScroller.isElementVisible(eventElement)) {
                                this.paperScroller.centerElement(eventElement);
                            }
                            break;
                        }
                        const predecessors = this.graph.getPredecessors(ftElement).reverse();
                        for (const predecessor of predecessors) {
                            if (predecessor.get('collapsed')) {
                                this.paper.trigger('element:expand', this.paper.findViewByModel(predecessor));
                            }
                        }
                        if (!this.paperScroller.isElementVisible(ftElement)) {
                            this.paperScroller.centerElement(ftElement);
                        }
                        break;
                    }
                }
            }
        }
    }

    public setStatusLinkedOccurrenceDna(occurrenceTatus: string): void {
        if (this.currentLinkedOccurrenceDna) {
            this.currentLinkedOccurrenceDna.occurrenceStatus = occurrenceTatus;
            this.saveLinkedOccurrenceDna();
        }
    }

    public setODNAStatusOfCurrentSelectedNode(status: 'FAILURE' | 'SUCCESS' | 'CLEAR'): void {
        if (this.currentLinkedOccurrenceDna && this.selection && this.selection.length > 0) {
            let currentNode = this.selection[0];
            if (currentNode.get('nodeClass') === 'EVENTNODE') {
                let node = this.currentLinkedOccurrenceDna.events.find((node) => node.id === currentNode.get('data').id);
                if (node) {
                    if (status === 'CLEAR') {
                        let idx = this.currentLinkedOccurrenceDna.events.findIndex((node) => node.id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.events.splice(idx, 1);
                        idx = this.currentLinkedOccurrenceDna.eventIds.findIndex((id) => id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.eventIds.splice(idx, 1);
                    } else {
                        node.failureStatus = status;
                    }
                } else {
                    if (status !== 'CLEAR') {
                        this.currentLinkedOccurrenceDna.events.push({ id: currentNode.get('data').id, justification: '', relevant: true, failureStatus: status });
                        this.currentLinkedOccurrenceDna.eventIds.push(currentNode.get('data').id);
                    }
                }
            } else if (currentNode.get('nodeClass') === 'FAULTTREENODE') {
                let node = this.currentLinkedOccurrenceDna.faultTreeNodes.find((node) => node.id === currentNode.get('data').id);
                if (node) {
                    if (status === 'CLEAR') {
                        let idx = this.currentLinkedOccurrenceDna.faultTreeNodes.findIndex((node) => node.id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.faultTreeNodes.splice(idx, 1);
                        idx = this.currentLinkedOccurrenceDna.faultTreeNodeIds.findIndex((id) => id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.faultTreeNodeIds.splice(idx, 1);
                    } else {
                        node.failureStatus = status;
                    }
                } else {
                    if (status !== 'CLEAR') {
                        this.currentLinkedOccurrenceDna.faultTreeNodes.push({ id: currentNode.get('data').id, justification: '', relevant: true, failureStatus: status });
                        this.currentLinkedOccurrenceDna.faultTreeNodeIds.push(currentNode.get('data').id);
                    }
                }
            }
            this.saveLinkedOccurrenceDna();
            this.drawDnaPath();
        }
    }

    public saveLinkedOccurrenceDna(occurrenceId?: number): void {
        if (this.currentLinkedOccurrenceDna !== null) {
            let dna = this.currentLinkedOccurrenceDna;
            if (occurrenceId && occurrenceId !== dna.occurrenceId) {
                // save dna path for a different occurrence
                // this is the case when user select 'Save DNA' from Occurrence Management Actions
                dna.occurrenceId = occurrenceId;
            }
            let reportMapping: ReportMappingUpdateDTO = {
                reportProcessingId: dna.reportProcessingId,
                changeReason: 'Updated via ODNA path editor',
                mappings: dna.events.map((event) => {
                    let irpNodeMapping: IRPNodeMappingDTO = {
                        reportProcessingId: dna.reportProcessingId,
                        nodeId: event.id, nodeType: 'EVENT',
                        confidence: 1, relevant: true,
                        justification: event.justification ? event.justification :  'Updated via ODNA path editor',
                        positiveInteraction: event.failureStatus === 'SUCCESS'
                    };
                    return irpNodeMapping;
                }).concat(dna.faultTreeNodes.map((ftNode) => {
                    let irpNodeMapping: IRPNodeMappingDTO = {
                        reportProcessingId: dna.reportProcessingId,
                        nodeId: ftNode.id,
                        nodeType: 'FAULT_TREE_NODE',
                        confidence: 1,
                        relevant: true,
                        justification: ftNode.justification  ? ftNode.justification :  'Updated via ODNA path editor',
                        positiveInteraction: ftNode.failureStatus === 'SUCCESS'
                    };
                    return irpNodeMapping;
                }))
            };
            irpService.replaceReportMappings(dna.irpId, dna.reportProcessingId, reportMapping).then(() => {
                toast.success('DNA path saved successfully');
            }).catch ((error) => {
                toast.error('Failed to save DNA path: ' + (error?.message || error));
            });
            // EventSequenceService.putLinkedOccurrenceDna(dna).then(() => {
            //     toast.success('DNA path saved successfully');
            // }).catch ((error) => {
            //     toast.error('Failed to save DNA path', error);
            // });
        }
    }

    public setLinkedOccurrenceDna(dna: SimpleLinkedOccurrenceDna | null): void {
        this.currentLinkedOccurrenceDna = null;  // important to prevent the dna path from being drawn during path expansion
        if (dna) {
            this.expandDnaPath(dna);
            this.currentLinkedOccurrenceDna = dna;
            this.drawDnaPath();   
        } else {
            this.unhighlightDnaPath();
        }
    }

    public clearLinkedOccurrenceDna(): void {
        this.unhighlightDnaPath();
    }

    public setIrp(irp: IntegratedRiskPicture): void {
        this.currentIntegratedRiskPicture = irp;
        this.clearLinkedOccurrenceDna();
    }
    
    public setDnaPath(dnaPath: joint.dia.Element[]): void {
        if (this.currentIntegratedRiskPicture === null) {
            toast.error('Please select an Integrated Risk Picture before setting the DNA path');
            return;
        }
        if (this.currentLinkedOccurrenceDna === null) {
            toast.error('Please select an Occurrence before setting the DNA path');
            return;
        }
        let dna: SimpleLinkedOccurrenceDna = {
            occurrenceId: this.currentLinkedOccurrenceDna.occurrenceId,
            reportProcessingId: this.currentLinkedOccurrenceDna.reportProcessingId,
            irpId: this.currentIntegratedRiskPicture.id,
            events: [],
            faultTreeNodes: [],
            eventSequenceId: this.eventSequence?.id!,
            occurrenceStatus: this.currentLinkedOccurrenceDna.occurrenceStatus,
            eventIds: [],
            faultTreeNodeIds: []
        };
        dna.events = dnaPath.filter((node) => node.get('nodeClass') === 'EVENTNODE').map((node) => {
            return { id: node.get('data').id, justification: '', relevant: true, failureStatus: 'FAILURE' };
        });
        dna.faultTreeNodes = dnaPath.filter((node) => node.get('nodeClass') === 'FAULTTREENODE').map((node) => {
            return { id: node.get('data').id, justification: '', relevant: true, failureStatus: 'FAILURE' };
        });

        if (dnaPath.length > 0) {
            let lastnode = dnaPath[dnaPath.length - 1];
            if (lastnode.get('nodeClass') === 'FAULTTREENODE' && (lastnode.get('data').nodeClass === 'BARRIER') || (lastnode.get('data').nodeClass === 'CIRCUMSTANTIAL')) {
                dna.faultTreeNodes[dna.faultTreeNodes.length - 1].failureStatus = 'SUCCESS';
            } else if (lastnode.get('nodeClass') === 'EVENTNODE') {
                if (lastnode.get('data').type === 'END') {
                    if (lastnode.get('data').outcome === 'POSITIVE') {
                        dna.events[dna.events.length - 1].failureStatus = 'SUCCESS';
                        let nodeParent = this.graph.getNeighbors(lastnode, { inbound: true })[0];
                        let idx = dna.events.findIndex((node) => node.id === nodeParent.get('data').id);
                        dna.events[idx].failureStatus = 'SUCCESS';
                    }
                }
            }

        }
        dna.eventIds = dna.events.map((node) => node.id);
        dna.faultTreeNodeIds = dna.faultTreeNodes.map((node) => node.id);
        this.setLinkedOccurrenceDna(dna);
        this.saveLinkedOccurrenceDna();
    }

    public unhighlightDnaPath(): void {
        if (this.dnaPath.length > 0) {
            this.dnaPath.forEach((element) => {
                let view = element.findView(this.paper);
                if (view) {
                    this.mask.remove(element.findView(this.paper));
                }
            });
            this.dnaPath = [];
        }
    }

    public highlightDnaPath(path: joint.dia.Cell[], mode: 'FAILURE' | 'SUCCESS'): void {
        
        path.forEach((element) => {
            if (element.get('hidden')) return;
            let view = element.findView(this.paper);
            if (!view) return;
            this.mask.add(view, { selector: element.isElement() ? 'root' : 'line' }, 'dna-highlighter', {
                deep: true,
                attrs: {
                    stroke: mode === 'FAILURE' ? '#FF4365' : '#008400',
                    'stroke-width': 8,                    
                    padding: 10
                }
            });
            
        });
    }

    public setPsfCounts({ psfIds, occurrenceIds, psfCounts }: { psfIds: number[], occurrenceIds: number[], psfCounts: EventSequencePsfTemplateItemStats }): void {
        // Use cached PSF node IDs for O(1) removal instead of O(n) graph scan
        canvasActions.clearAllPsfs(this);

        if (!this.psfTemplate) return;
        this.psfSelectedIds = psfIds;
        this.occurrenceSelectedIds = occurrenceIds;

        // Build lookup Map if not already built (lazy initialization for O(1) lookups)
        if (this.psfTemplateItemById.size === 0 && this.psfTemplate.psfTemplateItems) {
            for (const item of this.psfTemplate.psfTemplateItems) {
                this.psfTemplateItemById.set(item.id, item);
            }
        }

        if (psfCounts) {
            this.psfCounts.clear();
            psfCounts.faultTreeNodePsfTemplateItemStats.forEach((faultTreeNodePsfTemplateItemStat) => {
                const psfItemWithCounts = faultTreeNodePsfTemplateItemStat.psfTemplateItemStats.map((stat) => {
                    // Use pre-built Map for O(1) lookup instead of O(n) .find()
                    const psfItem = this.psfTemplateItemById.get(Number(stat.psfTemplateItemId));
                    return { ...psfItem, count: stat.numOccurrences } as PsfTemplateItemWithCount;
                });
                this.addPsfTemplateItemCounts(faultTreeNodePsfTemplateItemStat.faultTreeNodeId as number, psfItemWithCounts);

                let element = this.graph.getCell('ft-' + faultTreeNodePsfTemplateItemStat.faultTreeNodeId) as joint.dia.Element;
                if (element) {
                    setPsfNode(this, element, psfItemWithCounts);
                }
            });
        }
    }

    public setEventSequenceOccurrenceStats(stats: EventSequenceOccurrenceStats): void {
        this.eventSequenceOccurrenceStats = stats;

        // Pre-build lookup Maps for O(1) access (instead of O(n) .find() per element)
        this.odnaStatByEventId.clear();
        this.odnaStatByFtNodeId.clear();
        for (const stat of stats.eventOccurrenceStats) {
            this.odnaStatByEventId.set(stat.safetyModelElementId, stat);
        }
        for (const stat of stats.faultTreeNodeOccurrenceStats) {
            this.odnaStatByFtNodeId.set(stat.safetyModelElementId, stat);
        }

        canvasActions.clearOdnaStats(this);
        this.decoratedOdnaElements.clear();
        canvasActions.addOdnaStatToNodes(this, this.graph.getElements());
    }

    public clearEventSequenceOccurrenceStats(): void {
        this.eventSequenceOccurrenceStats = null;
        this.odnaStatByEventId.clear();
        this.odnaStatByFtNodeId.clear();
        canvasActions.clearOdnaStats(this);
        this.decoratedOdnaElements.clear();
    }

    public addPsfTemplateItemCounts(nodeId: number, psfTemplateItemCounts: PsfTemplateItemWithCount[]): void {
        this.psfCounts.set(nodeId, psfTemplateItemCounts);
    }

    public getPsfTemplateItemCounts(nodeId: number): PsfTemplateItemWithCount[] {
        return this.psfCounts.get(nodeId) || [];
    }

    public clearAllPsfTemplateItemCounts(): void {
        this.psfCounts.clear();
    }

    public deletePsfTemplateItemCounts(nodeId: number): void {
        this.psfCounts.delete(nodeId);
    }

    /**
     * Set the PSF template and clear the lookup Map to ensure fresh data.
     * Use this method instead of directly setting psfTemplate.
     */
    public setPsfTemplate(template: PsfTemplate | null): void {
        this.psfTemplate = template;
        // Clear lookup Map so it gets rebuilt with new template data
        this.psfTemplateItemById.clear();
    }

    // Lazy decoration tracking methods
    public isOdnaElementDecorated(elementId: string): boolean {
        return this.decoratedOdnaElements.has(elementId);
    }

    public markOdnaElementDecorated(elementId: string): void {
        this.decoratedOdnaElements.add(elementId);
    }

    public isTagElementDecorated(elementId: string): boolean {
        return this.decoratedTagElements.has(elementId);
    }

    public markTagElementDecorated(elementId: string): void {
        this.decoratedTagElements.add(elementId);
    }

    public clearTagDecorationTracking(): void {
        this.decoratedTagElements.clear();
    }

    public isCalloutElementDecorated(elementId: string): boolean {
        return this.decoratedCalloutElements.has(elementId);
    }

    public markCalloutElementDecorated(elementId: string): void {
        this.decoratedCalloutElements.add(elementId);
    }

    public clearCalloutDecorationTracking(): void {
        this.decoratedCalloutElements.clear();
    }

    public clearDecorationTracking(): void {
        this.decoratedOdnaElements.clear();
        this.decoratedTagElements.clear();
        this.decoratedCalloutElements.clear();
    }

    /**
     * Called on scroll to decorate newly visible elements.
     * Uses debouncing to avoid excessive decoration calls during scrolling.
     */
    public onScrollDecorateVisible(): void {
        if (this.decorationScrollTimeout) {
            clearTimeout(this.decorationScrollTimeout);
        }
        this.decorationScrollTimeout = setTimeout(() => {
            this.decorateVisibleElements();
        }, 100);
    }

    /**
     * Decorates elements currently visible in the viewport that haven't been decorated yet.
     */
    private decorateVisibleElements(): void {
        // Use the batched decoration function for efficiency
        canvasActions.decorateVisibleElements(this, { addToolsFlag: true, skipViewportFilter: false });
    }
}
